// src/lib/cat/autoAligner.ts
/**
 * Auto-Aligner Tool - FIXED VERSION
 * 
 * Builds Translation Memory from existing bilingual document pairs.
 * Uses multiple alignment strategies for best results.
 */

import { supabase } from '../supabase'

export interface AlignmentPair {
  sourceText: string
  targetText: string
  confidence: number // 0-100
  alignmentMethod: 'exact_position' | 'length_ratio' | 'lexical' | 'manual'
  sourceIndex: number
  targetIndex: number
}

export interface AlignmentResult {
  pairs: AlignmentPair[]
  stats: {
    totalSourceSegments: number
    totalTargetSegments: number
    alignedPairs: number
    highConfidence: number // > 80%
    mediumConfidence: number // 50-80%
    lowConfidence: number // < 50%
    averageConfidence: number
  }
}

export interface AlignmentOptions {
  sourceSegments: string[]
  targetSegments: string[]
  sourceLanguage: string
  targetLanguage: string
  minConfidence?: number
}

class AutoAligner {
  private readonly LENGTH_RATIO_TOLERANCE = 0.4 // 40% length difference allowed
  private readonly LEXICAL_THRESHOLD = 0.6 // 60% word overlap threshold

  /**
   * Main alignment method
   */
  async align(options: AlignmentOptions): Promise<AlignmentResult> {
    const { sourceSegments, targetSegments } = options
    
    // Clean segments
    const cleanedSource = sourceSegments.filter(s => s.trim().length > 0)
    const cleanedTarget = targetSegments.filter(s => s.trim().length > 0)

    let pairs: AlignmentPair[] = []

    // Strategy 1: Exact position alignment (if same count)
    if (cleanedSource.length === cleanedTarget.length) {
      pairs = this.alignByExactPosition(cleanedSource, cleanedTarget)
    } else {
      // Strategy 2: Length-based alignment
      pairs = this.alignByLengthRatio(cleanedSource, cleanedTarget)
      
      // Strategy 3: Lexical similarity for low-confidence pairs
      pairs = this.improveLowConfidencePairs(pairs, cleanedSource, cleanedTarget)
    }

    // Calculate statistics
    const stats = this.calculateStatistics(cleanedSource.length, cleanedTarget.length, pairs)

    return {
      pairs,
      stats
    }
  }

  /**
   * Save alignment pairs to Translation Memory
   */
  async saveToTM(
    pairs: AlignmentPair[],
    userId: string,
    sourceLanguage: string,
    targetLanguage: string,
    minConfidence: number = 60,
    onProgress?: (current: number, total: number) => void
  ): Promise<{ saved: number; errors: number }> {
    let saved = 0
    let errors = 0
    
    const filteredPairs = pairs.filter(p => p.confidence >= minConfidence)

    for (let i = 0; i < filteredPairs.length; i++) {
      const pair = filteredPairs[i]
      
      try {
        await supabase.from('cat_translation_memory').insert({
          user_id: userId,
          source_text: pair.sourceText,
          target_text: pair.targetText,
          source_language: sourceLanguage,
          target_language: targetLanguage,
          context: `auto-aligned (${Math.round(pair.confidence)}% confidence)`
        })
        
        saved++
      } catch (error) {
        console.error('Failed to save TM entry:', error)
        errors++
      }

      // Report progress every 10 pairs
      if (onProgress && (saved + errors) % 10 === 0) {
        onProgress(saved + errors, filteredPairs.length)
      }
    }

    // Final progress update
    if (onProgress) {
      onProgress(saved + errors, filteredPairs.length)
    }

    return { saved, errors }
  }

  /**
   * Export alignments to TMX format
   */
  async exportToTMX(
    pairs: AlignmentPair[],
    sourceLanguage: string,
    targetLanguage: string,
    minConfidence: number = 60
  ): Promise<string> {
    const filteredPairs = pairs.filter(p => p.confidence >= minConfidence)
    
    let tmx = '<?xml version="1.0" encoding="UTF-8"?>\n'
    tmx += '<tmx version="1.4">\n'
    tmx += '  <header creationtool="TranslyxCAT" creationtoolversion="1.0" segtype="sentence" '
    tmx += `srclang="${sourceLanguage}" adminlang="en" datatype="plaintext" />\n`
    tmx += '  <body>\n'

    for (let i = 0; i < filteredPairs.length; i++) {
      const pair = filteredPairs[i]
      
      tmx += '    <tu>\n'
      tmx += `      <tuv xml:lang="${sourceLanguage}">\n`
      tmx += `        <seg>${this.escapeXml(pair.sourceText)}</seg>\n`
      tmx += '      </tuv>\n'
      tmx += `      <tuv xml:lang="${targetLanguage}">\n`
      tmx += `        <seg>${this.escapeXml(pair.targetText)}</seg>\n`
      tmx += '      </tuv>\n'
      tmx += '    </tu>\n'
    }

    tmx += '  </body>\n'
    tmx += '</tmx>\n'

    return tmx
  }

  /**
   * Get statistics from alignment result (UTILITY METHOD)
   */
  getStatistics(result: AlignmentResult): AlignmentResult['stats'] {
    return result.stats
  }

  /**
   * Filter pairs by confidence threshold (UTILITY METHOD)
   */
  filterByConfidence(pairs: AlignmentPair[], minConfidence: number): AlignmentPair[] {
    return pairs.filter(p => p.confidence >= minConfidence)
  }

  /**
   * Strategy 1: Align by exact position (when counts match)
   */
  private alignByExactPosition(
    sourceSegments: string[],
    targetSegments: string[]
  ): AlignmentPair[] {
    const pairs: AlignmentPair[] = []

    for (let i = 0; i < sourceSegments.length; i++) {
      if (sourceSegments[i] && targetSegments[i]) {
        pairs.push({
          sourceText: sourceSegments[i],
          targetText: targetSegments[i],
          confidence: 85, // High confidence for position-based
          alignmentMethod: 'exact_position',
          sourceIndex: i,
          targetIndex: i
        })
      }
    }

    return pairs
  }

  /**
   * Strategy 2: Align by length ratio
   */
  private alignByLengthRatio(
    sourceSegments: string[],
    targetSegments: string[]
  ): AlignmentPair[] {
    const pairs: AlignmentPair[] = []
    const usedTargetIndices = new Set<number>()

    for (let i = 0; i < sourceSegments.length; i++) {
      const sourceText = sourceSegments[i]
      const sourceLen = sourceText.length
      
      let bestMatch: { index: number; ratio: number; confidence: number } | null = null

      for (let j = 0; j < targetSegments.length; j++) {
        if (usedTargetIndices.has(j)) continue

        const targetText = targetSegments[j]
        const targetLen = targetText.length
        const lengthRatio = Math.abs(1 - (sourceLen / targetLen))

        if (lengthRatio < this.LENGTH_RATIO_TOLERANCE && lengthRatio < bestMatch?.ratio) {
          bestMatch = {
            index: j,
            ratio: lengthRatio,
            confidence: Math.round((1 - lengthRatio / this.LENGTH_RATIO_TOLERANCE) * 80)
          }
        }
      }

      if (bestMatch) {
        usedTargetIndices.add(bestMatch.index)
        pairs.push({
          sourceText,
          targetText: targetSegments[bestMatch.index],
          confidence: bestMatch.confidence,
          alignmentMethod: 'length_ratio',
          sourceIndex: i,
          targetIndex: bestMatch.index
        })
      }
    }

    return pairs
  }

  /**
   * Strategy 3: Improve low-confidence pairs with lexical similarity
   */
  private improveLowConfidencePairs(
    pairs: AlignmentPair[],
    sourceSegments: string[],
    targetSegments: string[]
  ): AlignmentPair[] {
    const usedTargetIndices = new Set(pairs.map(p => p.targetIndex))
    const improvedPairs = [...pairs]

    for (let i = 0; i < sourceSegments.length; i++) {
      const existingPair = pairs.find(p => p.sourceIndex === i)
      if (existingPair && existingPair.confidence >= 70) continue

      const sourceText = sourceSegments[i]
      let bestMatch: { index: number; similarity: number } | null = null

      for (let j = 0; j < targetSegments.length; j++) {
        if (usedTargetIndices.has(j)) continue

        const targetText = targetSegments[j]
        const similarity = this.calculateLexicalSimilarity(sourceText, targetText)

        if (similarity >= this.LEXICAL_THRESHOLD) {
          if (!bestMatch || similarity > bestMatch.similarity) {
            bestMatch = { index: j, similarity }
          }
        }
      }

      if (bestMatch) {
        if (existingPair) {
          // Replace low-confidence pair
          const index = improvedPairs.indexOf(existingPair)
          improvedPairs[index] = {
            sourceText,
            targetText: targetSegments[bestMatch.index],
            confidence: Math.round(bestMatch.similarity * 100),
            alignmentMethod: 'lexical',
            sourceIndex: i,
            targetIndex: bestMatch.index
          }
        } else {
          // Add new pair
          improvedPairs.push({
            sourceText,
            targetText: targetSegments[bestMatch.index],
            confidence: Math.round(bestMatch.similarity * 100),
            alignmentMethod: 'lexical',
            sourceIndex: i,
            targetIndex: bestMatch.index
          })
        }
        usedTargetIndices.add(bestMatch.index)
      }
    }

    return improvedPairs
  }

  /**
   * Calculate lexical similarity between two segments
   */
  private calculateLexicalSimilarity(text1: string, text2: string): number {
    const words1 = text1.toLowerCase().split(/\s+/).filter(w => w.length > 2)
    const words2 = text2.toLowerCase().split(/\s+/).filter(w => w.length > 2)

    if (words1.length === 0 || words2.length === 0) return 0

    // Calculate word overlap
    const overlap = words1.filter(w => words2.includes(w)).length
    const maxLen = Math.max(words1.length, words2.length)

    if (maxLen === 0) return 0

    // Calculate number similarity (important for dates, amounts)
    const numbers1 = this.extractNumbers(text1)
    const numbers2 = this.extractNumbers(text2)
    let numberMatch = 0
    
    for (const num of numbers1) {
      if (numbers2.includes(num)) numberMatch++
    }

    // Calculate cognate similarity
    let cognateMatch = 0
    for (const word1 of words1) {
      for (const word2 of words2) {
        if (this.areCognates(word1, word2)) {
          cognateMatch++
          break
        }
      }
    }

    // Weighted combination
    const overlapScore = overlap / maxLen
    const numberScore = numbers1.length > 0 ? numberMatch / numbers1.length : 0
    const cognateScore = cognateMatch / maxLen

    return (overlapScore * 0.5) + (numberScore * 0.3) + (cognateScore * 0.2)
  }

  /**
   * Extract numbers from text
   */
  private extractNumbers(text: string): string[] {
    return (text.match(/\d+(?:\.\d+)?/g) || [])
      .map(n => parseFloat(n).toString())
  }

  /**
   * Check if two words are cognates (similar spelling)
   */
  private areCognates(word1: string, word2: string): boolean {
    if (word1.length < 4 || word2.length < 4) return false

    const minLen = Math.min(word1.length, word2.length)
    const maxLen = Math.max(word1.length, word2.length)

    if (word1 === word2) return true

    // Check if first 3 characters match
    if (minLen < 4) return false // Too short to be meaningful

    if (word1.substring(0, 3) === word2.substring(0, 3)) {
      // Allow some variation in length
      return maxLen - minLen <= 3
    }

    return false
  }

  /**
   * Calculate statistics
   */
  private calculateStatistics(
    sourceCount: number,
    targetCount: number,
    pairs: AlignmentPair[]
  ): AlignmentResult['stats'] {
    const highConfidence = pairs.filter(p => p.confidence > 80).length
    const mediumConfidence = pairs.filter(p => p.confidence >= 50 && p.confidence <= 80).length
    const lowConfidence = pairs.filter(p => p.confidence < 50).length
    
    const averageConfidence = pairs.length > 0
      ? Math.round(pairs.reduce((sum, p) => sum + p.confidence, 0) / pairs.length)
      : 0

    return {
      totalSourceSegments: sourceCount,
      totalTargetSegments: targetCount,
      alignedPairs: pairs.length,
      highConfidence,
      mediumConfidence,
      lowConfidence,
      averageConfidence
    }
  }

  /**
   * Escape XML special characters
   */
  private escapeXml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;')
  }
}

// Export singleton instance
export const autoAligner = new AutoAligner()